/**
 * @module core/aem
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ProductDetailsService } from '@experience/shop/services/product-details.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Wove } from 'aspect.js/';
import { ProductDetailsDefaultModel } from '@experience/shop/shared-browse/models/productDetails-default.model';
import {
  ItemDetail,
  Specification,
  SpecificationValues,
} from '@support/api/common/product.interface';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { PRODUCT_SPECIFICATION_CONST } from '@experience/shop/product-details/product-details.constants';
import { ObjectInterface } from '@support/api/common/api-response.interface';

/**
 * Product Specific Component initiator
 * @export
 * @class ProductSpecificationComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Wove({ moduleName: 'ProductSpecificationComponent' })
@Component({
  selector: 'tmo-product-specification',
  templateUrl: './product-specification.component.html',
  styleUrls: [],
})
export class ProductSpecificationComponent implements OnInit, OnDestroy {
  /**
   * Variable to store selectedItemDetails
   */
  public selectedItemDetails: Specification[];
  /**
   * Variable to store the pdpStaticContent from AEM and API
   */
  public pdpStaticContent: ConfigData;
  /**
   * Variable to store product of type ProductDetailsDefaultModel
   */
  public product: ProductDetailsDefaultModel;
  /**
   * to distroy the event OnDestroy life cycle
   */
  private ngUnsubscribe: Subject<void>;
  public selectedDevice: ItemDetail;
  public productSpecificationConst: ObjectInterface;

  constructor(
    private stateUtilService: StateStoreUtilsService,
    private productDetailsService: ProductDetailsService,
    private commonUtilService: CommonUtilService
  ) {
    this.ngUnsubscribe = new Subject<void>();
  }

  /**
   * Component lifecycle hook to initialize the component
   * @memberof ProductSpecificationComponent
   */
  public ngOnInit(): void {
    const offerId = this.stateUtilService.getSelectedOfferId();
    this.selectedDevice = {};
    this.productSpecificationConst = PRODUCT_SPECIFICATION_CONST;
    if (offerId) {
      this.product = this.stateUtilService.getSelectedDeviceDetails(offerId);
      this.selectedItemDetails = this.product.getProductSpecifications();
      this.formatFrequencySpecification();
      this.formatNetworkCapabilitiesSpecification();
    }
    this.productDetailsService.getselectedDeviceId$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: ItemDetail) => {
        this.selectedDevice = data;
      });

    this.productDetailsService
      .getProductDetailsContent()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((content: ConfigData) => {
        this.pdpStaticContent = content;
      });
  }

  /**
   * Function created to check if product selected is a device
   * @memberof ProductSpecificationComponent
   */
  public isWEACapable(): boolean {
    return (
      this.product.productSubType &&
      (this.product.productSubType.toLowerCase() === 'handset' ||
        this.product.productSubType.toLowerCase() === 'mobileinternet')
    );
  }

  /**
   * Unsubscribes for all subscriptions
   * @memberof ProductSpecificationComponent
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private formatFrequencySpecification(): void {
    const specifications = this.selectedItemDetails;
    specifications.forEach((item: Specification) => {
      if (item.group === this.productSpecificationConst.FREQUENCIES) {
        item.specificationValues = this.formatSpecificationValues(item.specificationValues);
      }
    });
  }

  private formatSpecificationValues(values: SpecificationValues[]): SpecificationValues[] {
    const specArray = [];
    const group = this.commonUtilService.groupByValues(values, 'name', 'value');
    for (const name in group) {
      if (name) {
        specArray.push({
          name: name,
          value: group[name].join(', '),
        });
      }
    }
    return specArray;
  }

  private formatNetworkCapabilitiesSpecification(): void {
    const specifications = this.selectedItemDetails;
    specifications.forEach((item: Specification) => {
      if (item.group === this.productSpecificationConst.NETWORK_CAPABILITY) {
        const filterSpecificationValue = item.specificationValues.filter(
          (data: SpecificationValues) => {
            return data.value === this.productSpecificationConst.TRUE;
          }
        );
        item.specificationValues = this.formatNetCapSpecificationValues(filterSpecificationValue);
      }
    });
  }

  private formatNetCapSpecificationValues(item: SpecificationValues[]): SpecificationValues[] {
    const result = item.map((data: SpecificationValues) => {
      return { ...data, name: data.name.replace('Capable', '').trim() };
    });
    return result;
  }
}
